import { useMutation } from "@/api/utils/useQuery"
import { HasuraMutations } from "@/api/hasura/mutations"
import { MutationResult } from "@/api/utils/types"
import {
  InsertEmailTemplateMutation,
  InsertEmailTemplateMutationVariables,
  InsertEmailTemplateAsLiveMutation,
  InsertEmailTemplateAsLiveMutationVariables,
  SetLiveEmailTemplateVersionMutation,
  SetLiveEmailTemplateVersionMutationVariables,
  Email_Templates_Insert_Input,
} from "@/api/hasura/generated/graphql"
import { ICreateEmailTemplateInput } from "@/models/admin/emailTemplates/emailTemplates.types"
import { formatServerDateTimeNow } from "@/helpers/dateHelper"

type InsertEmailTemplateResponse =
  InsertEmailTemplateMutation["insert_email_templates_one"]

type InsertEmailTemplateAsLiveResponse =
  InsertEmailTemplateAsLiveMutation["inserted"]

type SetLiveEmailTemplateVersionResponse =
  SetLiveEmailTemplateVersionMutation["setLive"]

function toInsertInput(
  data: ICreateEmailTemplateInput,
): Email_Templates_Insert_Input {
  return {
    communication_id: data.communicationId,
    version: data.version,
    is_live: data.isLive,
    subject: data.subject,
    plain_content: data.plainContent,
    html_content: data.htmlContent,
    modified_by: data.modifiedBy,
    modified_reason: data.modifiedReason,
  }
}

export interface IEmailTemplatesMutator {
  createTemplate(): MutationResult<
    ICreateEmailTemplateInput,
    InsertEmailTemplateResponse
  >
  createTemplateAsLive(): MutationResult<
    ICreateEmailTemplateInput,
    InsertEmailTemplateAsLiveResponse
  >
  setLiveVersion(): MutationResult<
    { id: number; communicationId: number },
    SetLiveEmailTemplateVersionResponse
  >
}

class EmailTemplatesMutatorClass implements IEmailTemplatesMutator {
  createTemplate(): MutationResult<
    ICreateEmailTemplateInput,
    InsertEmailTemplateResponse
  > {
    return useMutation({
      ...HasuraMutations.emailTemplates.insert(),
      serializerFn: (
        data: ICreateEmailTemplateInput,
      ): InsertEmailTemplateMutationVariables => ({
        input: toInsertInput({ ...data, isLive: false }),
      }),
    })
  }

  createTemplateAsLive(): MutationResult<
    ICreateEmailTemplateInput,
    InsertEmailTemplateAsLiveResponse
  > {
    return useMutation({
      ...HasuraMutations.emailTemplates.insertAsLive(),
      serializerFn: (
        data: ICreateEmailTemplateInput,
      ): InsertEmailTemplateAsLiveMutationVariables => ({
        communicationId: data.communicationId,
        input: {
          ...toInsertInput({ ...data, isLive: true }),
          published_at: formatServerDateTimeNow(),
        },
      }),
    })
  }

  setLiveVersion(): MutationResult<
    { id: number; communicationId: number },
    SetLiveEmailTemplateVersionResponse
  > {
    return useMutation({
      ...HasuraMutations.emailTemplates.setLiveVersion(),
      serializerFn: (data: {
        id: number
        communicationId: number
      }): SetLiveEmailTemplateVersionMutationVariables => ({
        id: data.id,
        communicationId: data.communicationId,
        publishedAt: formatServerDateTimeNow(),
      }),
    })
  }
}

export const EmailTemplatesMutator = new EmailTemplatesMutatorClass()
